import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router";
import { useAuth } from "../../auth/AuthProvider";
import { ErrorSummary, PasswordField } from "../../components/form";
import { useI18n } from "../../i18n/I18nProvider";
import type { MessageKey } from "../../i18n/messages";
import { AuthLayout } from "./AuthLayout";

export function ResetPasswordPage() {
  const { t } = useI18n();
  const auth = useAuth();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const linkError = params.get("error") || !token;
  // From "Tu tienda está lista" (a shop pokefans set up): after the password, straight to the shop.
  const shop = params.get("tienda") === "1";
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>(linkError ? { form: "auth.errors.invalidToken" } : {});
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (ev: FormEvent) => {
    ev.preventDefault();
    setAttempt((n) => n + 1);
    if (password.length < 8) {
      setErrors({ password: "auth.errors.password" });
      return;
    }
    setBusy(true);
    const r = await auth.resetPassword(token, password);
    setBusy(false);
    if (r.ok) setDone(true);
    else setErrors({ form: r.error });
  };

  return (
    <AuthLayout title={t(shop ? "auth.shopInviteTitle" : "auth.resetTitle")}>
      {done ? (
        <>
          <p role="status">{t("auth.resetDone")}</p>
          <Link to={shop ? "/ingresar?next=%2Fmi-tienda" : "/ingresar"} className="btn btn-primary btn-block">
            {t("auth.submitLogin")}
          </Link>
        </>
      ) : (
        <form onSubmit={submit} noValidate>
          <ErrorSummary errors={errors} fieldIds={{ password: "reset-password" }} focusKey={attempt} />
          {linkError ? (
            <Link to="/recuperar" className="btn btn-outline btn-block">
              {t("auth.forgotTitle")}
            </Link>
          ) : (
            <>
              <PasswordField
                id="reset-password"
                label={t("auth.newPassword")}
                autoComplete="new-password"
                hint={t("auth.passwordHint")}
                value={password}
                error={errors.password ? t(errors.password as MessageKey) : null}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={busy}>
                {busy ? t("auth.working") : t("auth.submitReset")}
              </button>
            </>
          )}
        </form>
      )}
    </AuthLayout>
  );
}
